import { useContext, useEffect, useMemo, useState } from "react"
import {
    Autocomplete, Box, Button, Dialog, DialogActions, DialogContent,
    Divider, Grid, IconButton, Paper, Stack, Typography, useMediaQuery, useTheme
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import RemoveIcon from "@mui/icons-material/Remove"
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded"
import { BranchRead } from "../../global/API"
import { CstmTextField } from "../../../../core/global/forms/CstmTextField"
import { CompanyProductRead, ToolUser } from "../../global/API"
import { ToolContext } from "../../../../core/context/ToolContext"
import { useHistory } from "react-router-dom/cjs/react-router-dom.min"
import { OrderSave, BranchUserRead } from "../../global/API"

const createRow = () => ({ item: null, quantity: "", qtyError: "" })

// Valid: integers only (positive or negative), no decimals, no "--", no leading zeros except "0" itself
// Allowed while typing: "", "-" (partial negative entry)
const validateQuantity = (val) => {
    if (val === "" || val === "-") return ""                          // still typing — no error yet
    if (/--/.test(val)) return "Double negatives are not allowed"    // catches "--" etc.
    if (!/^-?[1-9]\d*$/.test(val)) return "Enter a whole number (no decimals)"
    return ""
}

export const Form = () => {
    const { adjustment_state, tool_state } = useContext(ToolContext)
    const { infiniteState, productList, productSearchList, listLoader, searchInput, branchData } = adjustment_state
    const { __SESSION } = tool_state


    const [formData, setFormData] = useState({ branch_id: 0, transaction_date: "", staff_id: 0, items: [createRow()], remarks: "" })
    const [previewOpen, setPreviewOpen] = useState(false)
    const [submitLoader, setSubmitLoader] = useState(false)
    const [toolUsers, setToolUsers] = useState(false)

    const nav = useHistory()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

    const role = __SESSION.data.uaccess[0].role_name
    const tool_id = process.env.REACT_APP_TID_BIZ;

    const FetchCompanyProducts = async (branchId, filter, offset = 0, dataType = "string") => {
        productList.set([])
        productSearchList.set([])
        if (offset === 0) {
            listLoader.set((prev) => ({ ...prev, isloading: true }))
            infiniteState.set((prev) => ({ ...prev, infiniteLoad: true }))
        }
        const qsp = {
            company_id: __SESSION?.data?.uaccess?.[0]?.company_id,
            filter,
            limit: infiniteState.data.limit,
            offset,
            search: searchInput.data,
            dataType,
            branch_id: branchId,
        }
        const res = await CompanyProductRead(qsp)
        if (res) {
            if (res.length === 0) {
                infiniteState.set((prev) => ({ ...prev, infiniteLoad: false, offset: 0 }))
            } else {
                if (res.length < infiniteState.data.limit) {
                    infiniteState.set((prev) => ({ ...prev, infiniteLoad: false, offset: 0 }))
                } else {
                    infiniteState.set((prev) => ({ ...prev, offset: res.length + productList.data.length }))
                }
                if (searchInput.data === "") {
                    productList.set((prev) => [...prev, ...res])
                } else {
                    productSearchList.set((prev) => [...prev, ...res])
                }
            }
            listLoader.set((prev) => ({ ...prev, isloading: false }))
        }
    }

    const __init = async () => {
        let res
        let qsp = {
            user_id:  parseInt(atob(__SESSION.data.ainfo.aid).split('_', 2)[1]),
            company_id: __SESSION.data.uaccess[0].company_id,
            filter: 'active',
            tool_id: tool_id,
        }
        res = role === "Supervisor" ? await BranchUserRead(qsp) : await BranchRead(__SESSION.data.uaccess[0].company_id, 'active')
        if (res) branchData.set(role === "Supervisor" ? res : [{company_branch_id:0, name:'All', code:'000', active:1}, ...res])

        let res_user = await ToolUser(__SESSION.data.uaccess[0].company_id)
        if (res_user) setToolUsers(res_user.users)
    }

    const handleBranchChange = (value) => {
        const branchId = value?.company_branch_id ?? 0
        setFormData({ branch_id: branchId, transaction_date: "", items: [createRow()], remarks: "" })
        productList.set([])
        productSearchList.set([])
        if (branchId && branchId !== 0) FetchCompanyProducts(branchId, "branch-product")
    }


    useEffect(() => {
        __init()
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (__SESSION?.data?.uaccess?.[0]?.company_id && formData.branch_id && formData.branch_id !== 0) {
            FetchCompanyProducts(formData.branch_id, "branch-product")
        }
        // eslint-disable-next-line
    }, [__SESSION?.data?.uaccess])

    const productOptions = useMemo(() => {
        return searchInput.data === "" ? productList.data || [] : productSearchList.data || []
    }, [searchInput.data, productList.data, productSearchList.data])

    const getProductLabel = (option) => {
        if (!option) return ""
        return option.alias && option.alias.trim() !== ""
                ? option.alias
                : `${option.sub_category_name} ${option.product_name}`
    }

    const getProductKey = (option) => {
        if (!option) return ""
        return option.id || option.code || getProductLabel(option)
    }

    // Get the product's base quantity from product setup (e.g. quantity per unit)
    // This is used as the multiplier: user_input_qty * product_base_qty = actual_qty
    const getProductBaseQty = (option) => {
        if (!option) return 1
        return parseFloat(option.quantity || option.base_quantity || option.qty || 1)
    }

    const getProductPrice = (option) => {
        if (!option) return 0
        return parseFloat(option.price || option.unit_price || option.selling_price || 0)
    }

    const updateRow = (index, field, value) => {
        setFormData((prev) => {
            const updatedRows = [...prev.items]
            const update = { ...updatedRows[index], [field]: value }
            if (field === "quantity") update.qtyError = validateQuantity(value)
            updatedRows[index] = update
            return { ...prev, items: updatedRows }
        })
    }

    const addRow = () => setFormData((prev) => ({ ...prev, items: [...prev.items, createRow()] }))

    const removeRow = (index) => {
        setFormData((prev) => {
            if (prev.items.length === 1) return prev
            return { ...prev, items: prev.items.filter((_, i) => i !== index) }
        })
    }

    const selectedBranch = branchData.data.find((f) => f.company_branch_id === formData.branch_id) || null

    const formatDate = (dateStr) => {
        if (!dateStr) return "—"
        return new Date(dateStr).toLocaleDateString("en-PH", { year: "numeric", month: "long", day: "numeric" })
    }

    const buildCart = () => {
        return formData.items
            .filter((row) => {
                if (!row.item || row.quantity === "" || row.quantity === "-") return false
                if (row.qtyError) return false
                const parsed = parseInt(row.quantity, 10)
                return !isNaN(parsed) && parsed !== 0
            })
            .map((row) => {
                const price = getProductPrice(row.item)
                const baseQty = getProductBaseQty(row.item)
                const userQty = parseInt(row.quantity, 10)
                // const actualQty = userQty * baseQty   // e.g. -2 entered * 2 base = -4
                const amount = userQty * price

                return {
                    company_branch_product_id: getProductKey(row.item),
                    quantity: userQty,
                    price,
                    amount,
                }
            })
    }

    const handleConfirmSubmit = async() => {
        const cart = buildCart()
        const total = cart.reduce((prev, current) => prev + current.amount, 0)

        const body = {
            branch_id: formData.branch_id,
            user_id:  parseInt(atob(__SESSION.data.ainfo.aid).split('_', 2)[1]),
            total,
            cart,
            remarks: formData.remarks,
            transaction_date: formData.transaction_date,
            type: 'adjustment',
            staff_id: formData.staff_id
        }

        // console.log("Reversal Adjustment Payload:", JSON.stringify(body, null, 2))
        setSubmitLoader(true)
        let res = await OrderSave(body)
        if (res){
            setSubmitLoader(false)
            setPreviewOpen(false)
        }
        nav.push('/')

    }

    // For preview display
    const previewCart = buildCart()
    const previewTotal = previewCart.reduce((sum, r) => sum + r.amount, 0)

    // Form is valid when:
    // 1. A branch is selected
    // 2. A transaction date is set
    // 3. At least one item row has a valid item + non-zero integer qty with no errors
    // 4. No row has a qty error at all (catches partially filled broken rows)
    const isFormValid =
        formData.branch_id !== 0 &&
        formData.transaction_date !== "" &&
        formData.items.every((row) => !row.qtyError) &&
        formData.items.some((row) => {
            if (!row.item || row.quantity === "" || row.quantity === "-") return false
            if (row.qtyError) return false
            const parsed = parseInt(row.quantity, 10)
            return !isNaN(parsed) && parsed !== 0
        })

    

    const today = new Date();
    const minimumDate = new Date();
    minimumDate.setDate(today.getDate() - 2);

    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: { xs: "flex-start", sm: "center" },
                bgcolor: "#ffffff",
                p: { xs: 0, sm: 2 },
                height: "100%"
            }}
        >
            <Stack
                spacing={2.5}
                sx={{
                    width: "100%",
                    maxWidth: 680,
                    border: { xs: "none", sm: "1px solid #e0e0e0" },
                    borderRadius: { xs: 0, sm: 3 },
                    bgcolor: "#fff",
                    boxShadow: { xs: "none", sm: "0 2px 16px rgba(0,0,0,0.06)" },
                    py: 10,
                    px: 2
                }}
            >
                {/* Header */}
                <Box>
                    <Typography fontWeight={700} fontSize={{ xs: "1.1rem", sm: "1.25rem" }} color="#111">
                        Reversal Adjustment
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                        Fill in the details below to process a stock reversal.
                    </Typography>
                </Box>

                <Divider />

                {/* Branch & Date */}
                
                <Box width="100%" display="flex" gap={2}>
                    <Autocomplete
                        fullWidth
                        options={branchData.data}
                        getOptionLabel={(option) => option.name || ""}
                        onChange={(_, value) => handleBranchChange(value)}
                        value={selectedBranch}
                        renderOption={(props, option) => <li {...props} key={option.company_branch_id}>{option.name}</li>}
                        renderInput={(params) => <CstmTextField {...params} label="Branch" size={isMobile ? "small" : "medium"} />}
                    />
                <CstmTextField
                    label="Transaction Date"
                    type="date"
                    fullWidth
                    size={isMobile ? "small" : "medium"}
                    value={formData.transaction_date}
                    onChange={(e) => setFormData((prev) => ({ ...prev, transaction_date: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                        max: today.toISOString().split("T")[0],
                        min: minimumDate.toISOString().split("T")[0]
                    }}
                />
                </Box>
                
                <Autocomplete
                    fullWidth
                    options={toolUsers || []}
                    getOptionLabel={(option) =>
                        option.id === 0
                            ? 'All'
                            : `${option.first_name} ${option.last_name}`.trim()
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={(_, val) =>
                        setFormData((prev) => ({ ...prev, staff_id: val?.id ?? 0 }))
                    }
                    renderInput={(params) => (
                        <CstmTextField
                            {...params}
                            label="Staff"
                            size={isMobile ? "small" : "medium"}
                        />
                    )}
                />

                {/* Items */}
                <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
                    <Stack spacing={1.5}>
                        <Typography fontWeight={600} fontSize="0.95rem">Items for Reversal</Typography>

                        {formData.items.map((row, index) => (
                            <Box
                                key={`item-${index}`}
                                display="flex"
                                flexWrap="wrap"
                                gap={1.5}
                                alignItems="center"
                            >
                                {/* Item selector */}
                                <Box flex="1 1 100%" maxWidth={{ sm: "50%" }}>
                                    <Autocomplete
                                        options={productOptions}
                                        value={row.item}
                                        onChange={(_, newValue) => updateRow(index, "item", newValue)}
                                        getOptionLabel={getProductLabel}
                                        isOptionEqualToValue={(option, value) => getProductKey(option) === getProductKey(value)}
                                        renderInput={(params) => (
                                            <CstmTextField
                                                {...params}
                                                label="Item"
                                                placeholder="Select item"
                                                fullWidth
                                                size={isMobile ? "small" : "medium"}
                                            />
                                        )}
                                    />
                                </Box>

                                {/* Quantity */}
                                <Box flex="1 1 65%" maxWidth={{ sm: "25%" }}>
                                    <CstmTextField
                                        label="Quantity"
                                        type="number"
                                        fullWidth
                                        size={isMobile ? "small" : "medium"}
                                        value={row.quantity}
                                        onChange={(e) => {
                                            const raw = e.target.value
                                            if (/--/.test(raw) || raw.includes(".")) return
                                            updateRow(index, "quantity", raw)
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "." || e.key === "e" || e.key === "E") e.preventDefault()
                                            if (e.key === "-" && String(row.quantity).includes("-")) e.preventDefault()
                                        }}
                                        error={!!row.qtyError}
                                        inputProps={{ min: undefined }}
                                    />
                                </Box>

                                {/* Actions */}
                                <Box
                                    flex="1 1 30%"
                                    maxWidth={{ sm: "15%" }}
                                    display="flex"
                                    gap={0.5}
                                    justifyContent="flex-end"
                                >
                                    <IconButton
                                        size={isMobile ? "small" : "medium"}
                                        color="primary"
                                        onClick={addRow}
                                        sx={{ border: "1px solid", borderColor: "primary.main", borderRadius: 1.5 }}
                                    >
                                        <AddIcon fontSize={isMobile ? "small" : "medium"} />
                                    </IconButton>

                                    <IconButton
                                        size={isMobile ? "small" : "medium"}
                                        color="error"
                                        onClick={() => removeRow(index)}
                                        disabled={formData.items.length === 1}
                                        sx={{
                                            border: "1px solid",
                                            borderColor: formData.items.length === 1 ? "#e0e0e0" : "error.main",
                                            borderRadius: 1.5
                                        }}
                                    >
                                        <RemoveIcon fontSize={isMobile ? "small" : "medium"} />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                </Paper>

                {/* Remarks */}
                <CstmTextField
                    label="Remarks"
                    placeholder="Enter reason for reversal..."
                    multiline
                    rows={3}
                    fullWidth
                    size={isMobile ? "small" : "medium"}
                    value={formData.remarks}
                    onChange={(e) => setFormData((prev) => ({ ...prev, remarks: e.target.value }))}
                />

                {/* Actions */}
                <Box sx={{ display: "flex", gap: 1.5, pt: 0.5 }}>
                    <Button
                        variant="contained"
                        onClick={() => setPreviewOpen(true)}
                        fullWidth
                        disabled={!isFormValid}
                        size={isMobile ? "medium" : "large"}
                        sx={{ py: { xs: 1, sm: 1.5 }, borderRadius: 2, boxShadow: 0, fontWeight: 600, ":hover": { boxShadow: 0 } }}
                    >
                        Submit
                    </Button>
                </Box>
            </Stack>

            {/* Confirm Dialog */}
            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: { xs: 0, sm: 3 },
                        p: { xs: 0, sm: 1 },
                    },
                }}
            >
                <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 3, sm: 2 } }}>
                    <Stack spacing={2}>
                        {/* Warning header */}
                        <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" gap={1} pt={1}>
                            <Box sx={{ bgcolor: "#fff8e1", borderRadius: "50%", p: 1.5, display: "flex" }}>
                                <WarningAmberRoundedIcon sx={{ fontSize: { xs: 36, sm: 40 }, color: "#f59e0b" }} />
                            </Box>
                            <Typography fontWeight={700} fontSize={{ xs: "1rem", sm: "1.1rem" }}>
                                Confirm Reversal Adjustment
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Please review the details below carefully before confirming.
                            </Typography>
                        </Box>

                        <Divider />

                        {/* Summary fields */}
                        <Grid container spacing={0.75} rowSpacing={1}>
                            {[
                                { label: "Branch", value: selectedBranch?.name || "—" },
                                { label: "Transaction Date", value: formatDate(formData.transaction_date) },
                                { label: "Remarks", value: formData.remarks || "—" },
                            ].map(({ label, value }) => (
                                <>
                                    <Grid item xs={5}>
                                        <Typography variant="body2" color="text.secondary" fontWeight={500}>{label}</Typography>
                                    </Grid>
                                    <Grid item xs={7}>
                                        <Typography variant="body2" fontWeight={600}>{value}</Typography>
                                    </Grid>
                                </>
                            ))}
                        </Grid>

                        <Divider />

                        {/* Items table */}
                        <Box>
                            <Typography variant="body2" fontWeight={600} mb={1}>Items for Reversal</Typography>
                            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                                {/* Table header */}
                                <Box sx={{ bgcolor: "#f9fafb", px: 2, py: 1 }}>
                                    <Grid container>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" fontWeight={700} color="text.secondary">ITEM</Typography>
                                        </Grid>
                                        <Grid item xs={6} textAlign="right">
                                            <Typography variant="caption" fontWeight={700} color="text.secondary">INPUT QTY</Typography>
                                        </Grid>
                                        {/* <Grid item xs={4} textAlign="right">
                                            <Typography variant="caption" fontWeight={700} color="text.secondary">ACTUAL QTY</Typography>
                                        </Grid> */}
                                    </Grid>
                                </Box>
                                <Divider />

                                {formData.items.map((row, index) => {
                                    const baseQty = getProductBaseQty(row.item)
                                    const userQty = parseInt(row.quantity, 10) || 0
                                    const actualQty = userQty * baseQty
                                    const isNegative = actualQty < 0

                                    return (
                                        <Box key={index}>
                                            <Box px={2} py={1}>
                                                <Grid container alignItems="center">
                                                    <Grid item xs={6}>
                                                        {row.item ? (
                                                            <Typography variant="body2">{getProductLabel(row.item)}</Typography>
                                                        ) : (
                                                            <Typography component="span" variant="body2" color="error.light" fontStyle="italic">
                                                                No item selected
                                                            </Typography>
                                                        )}
                                                    </Grid>
                                                    <Grid item xs={6} textAlign="right">
                                                        <Typography variant="body2">{row.quantity || "—"}</Typography>
                                                    </Grid>
                                                    {/* <Grid item xs={4} textAlign="right">
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={600}
                                                            color={isNegative ? "error.main" : "text.primary"}
                                                        >
                                                            {row.item && row.quantity && !row.qtyError ? actualQty : "—"}
                                                        </Typography>
                                                    </Grid> */}
                                                </Grid>
                                            </Box>
                                            {index < formData.items.length - 1 && <Divider />}
                                        </Box>
                                    )
                                })}

                                {/* Total row */}
                                <Divider />
                                <Box sx={{ bgcolor: "#f9fafb", px: 2, py: 1 }}>
                                    <Grid container>
                                        <Grid item xs={8}>
                                            <Typography variant="body2" fontWeight={700}>Total Amount</Typography>
                                        </Grid>
                                        <Grid item xs={4} textAlign="right">
                                            <Typography variant="body2" fontWeight={700} color={previewTotal < 0 ? "error.main" : "primary.main"}>
                                                ₱{previewTotal.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Paper>
                        </Box>

                        {/* Warning note */}
                        <Box sx={{ bgcolor: "#fff8e1", border: "1px solid #fcd34d", borderRadius: 2, px: 2, py: 1.5 }}>
                            <Typography variant="caption" color="#92400e">
                                This adjustment cannot be undone once submitted. Make sure all details are correct.
                            </Typography>
                        </Box>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 3, sm: 2 }, gap: 1 }}>
                    <Button
                        onClick={() => setPreviewOpen(false)}
                        fullWidth
                        variant="contained"
                        sx={{
                            borderRadius: 2, boxShadow: 0,
                            bgcolor: "#e8e8e8", color: "#333",
                            fontWeight: 600,
                            ":hover": { boxShadow: 0, bgcolor: "#d5d5d5" },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmSubmit}
                        fullWidth
                        variant="contained"
                        color="warning"
                        sx={{ borderRadius: 2, boxShadow: 0, fontWeight: 600, ":hover": { boxShadow: 0 } }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}